/**
 * The sound cues of the five repetitions (R13) — Sobhan's own recordings
 * (Telegram, 2026-10-06), shipped as static files under `public/sounds/`:
 *
 *   rep_wrong     a repetition that did not count ("❌ غلط انجام دادن تمرین")
 *   rep_ok        repetitions 1–4 that counted ("✅ مراحل یک الی چهار")
 *   rep_final_ok  the 5th that counted ("✅ مرحلهٔ آخر، پنجم") — with the confetti
 *
 * Each exists as Ogg/Opus (small, every current browser but older Safari)
 * and MP3 (everything else); the browser's own `canPlayType` picks. They
 * play on the practice's AudioContext — already unlocked by the tap on the
 * microphone — because an `<audio>` started from a timer is exactly what iOS
 * refuses to play.
 */

export const CUES = {
  wrong: 'rep_wrong',
  ok: 'rep_ok',
  final: 'rep_final_ok',
}

/** Which cue a checked repetition gets: rejected, counted, or the counted last one. */
export function cueFor({ counted, last }) {
  if (!counted) return CUES.wrong
  return last ? CUES.final : CUES.ok
}

/** The file to fetch for a cue: Ogg/Opus where the browser says it can, MP3 otherwise. */
export function cueUrl(name, canPlay = defaultCanPlay) {
  return `/sounds/${name}.${canPlay('audio/ogg; codecs="opus"') ? 'ogg' : 'mp3'}`
}

function defaultCanPlay(type) {
  if (typeof Audio === 'undefined') return false
  return new Audio().canPlayType(type) !== ''
}

/** Decoded cues per AudioContext: decoded once, played as often as needed. */
const decoded = new WeakMap()

async function bufferFor(context, name, fetchImpl) {
  let cache = decoded.get(context)
  if (!cache) {
    cache = new Map()
    decoded.set(context, cache)
  }
  if (!cache.has(name)) {
    cache.set(
      name,
      fetchImpl(cueUrl(name))
        .then((response) => {
          if (!response.ok) throw new Error(`cue ${name}: HTTP ${response.status}`)
          return response.arrayBuffer()
        })
        .then((bytes) => context.decodeAudioData(bytes))
        .catch((error) => {
          cache.delete(name) // try again next time
          throw error
        }),
    )
  }
  return cache.get(name)
}

/** Fetches and decodes the three cues ahead of the first repetition, so the first one plays on time. */
export function preloadCues(context, { fetchImpl = globalThis.fetch } = {}) {
  if (!context || !fetchImpl) return
  for (const name of Object.values(CUES)) void bufferFor(context, name, fetchImpl).catch(() => {})
}

/**
 * Plays a cue and resolves with its length in seconds once it has started —
 * what the caller waits before the reference plays again. A cue that cannot
 * be had resolves 0: silence is never a reason to hold the practice up.
 */
export async function playCue(context, name, { fetchImpl = globalThis.fetch } = {}) {
  if (!context || typeof context.createBufferSource !== 'function' || !fetchImpl) return 0
  try {
    const buffer = await bufferFor(context, name, fetchImpl)
    const source = context.createBufferSource()
    source.buffer = buffer
    source.connect(context.destination)
    source.start()
    return buffer.duration
  } catch {
    return 0
  }
}
